import type { ComponentPropsWithoutRef, ReactNode } from 'react';
import { createContext, memo, useContext, useMemo } from 'react';
import styled, { css } from 'styled-components';
import type { AssetAggregateCardAspectRatioEnum } from '@frameio/graphql-client/generated';
import { theme, useMeasure } from '@frameio/vapor';
import { useAssetAggregateLayout } from '../contexts';
import { aspectRatioToString } from '../utils';

const rowContainerStyles = css`
  border-radius: ${theme.radii[4]};
  height: ${theme.spaces[24]};
`;

const cardContainerStyles = css`
  border-radius: ${theme.radii[8]};
`;

export const ThumbnailContainer = memo(
  ({
    children,
    $aspectRatio,
    height,
    ...rest
  }: {
    children: ReactNode;
    $aspectRatio: AssetAggregateCardAspectRatioEnum;
    height?: number | undefined;
  } & ComponentPropsWithoutRef<'div'>) => {
    const [ref, rect] = useMeasure();
    const isList = useAssetAggregateLayout() === 'LIST';
    return (
      <StyledThumbnailContainer
        ref={ref}
        {...rest}
        $aspectRatio={$aspectRatio}
        $isList={isList}
        style={height ? { height } : undefined}
      >
        <ThumbnailContainerContext.Provider
          value={useMemo(
            () => ({
              height: rect.height,
              width: rect.width,
            }),
            [rect.height, rect.width]
          )}
        >
          {children}
        </ThumbnailContainerContext.Provider>
      </StyledThumbnailContainer>
    );
  }
);

export const ThumbnailContainerContext = createContext({
  width: 0,
  height: 0,
});

export const useThumbnailContainerDimensions = () =>
  useContext(ThumbnailContainerContext);

export const StyledThumbnailContainer = styled.div<{
  $aspectRatio: AssetAggregateCardAspectRatioEnum;
  $isList?: boolean;
}>`
  aspect-ratio: ${(p) => aspectRatioToString[p.$aspectRatio]};
  overflow: hidden;
  width: 100%;
  ${(p) => (p.$isList ? rowContainerStyles : cardContainerStyles)};
`;
